"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CurrentStepHeaderControllerScrollContainer = exports.CurrentStepHeaderControllerConnection = exports.ControllerConnection = exports.CurrentStepHeaderController = exports.useCurrentStepHeaderContext = void 0;
const react_1 = __importStar(require("react"));
const lodash_1 = require("lodash");
const react_intersection_observer_1 = require("react-intersection-observer");
const defaultHeaderContextValue = {
    shouldCondenseAppBar: false,
    shouldShowAppBarStepHeader: false,
    isAppBarStepHeaderHidden: true,
};
const CurrentStepHeaderContext = react_1.createContext(defaultHeaderContextValue);
exports.useCurrentStepHeaderContext = () => react_1.useContext(CurrentStepHeaderContext);
const DEFAULT_CONTEXT_ERROR_MESSAGE = `useCurrentStepHeaderContext() does not work with the default context. Ensure that a <CurrentStepHeaderController> exists in the component hierarchy.`;
const defaultControllerContextValue = {
    get ref() {
        throw new Error(DEFAULT_CONTEXT_ERROR_MESSAGE);
    },
    get containerRef() {
        throw new Error(DEFAULT_CONTEXT_ERROR_MESSAGE);
    },
    get doSetup() {
        throw new Error(DEFAULT_CONTEXT_ERROR_MESSAGE);
    },
    get doReset() {
        throw new Error(DEFAULT_CONTEXT_ERROR_MESSAGE);
    },
};
const ControllerContext = react_1.createContext(defaultControllerContextValue);
const condenseAppBarThreshold = 0;
const showAppBarStepHeaderThreshold = 0.5;
const threshold = [condenseAppBarThreshold, showAppBarStepHeaderThreshold, 1.0];
const wasScrolledBySystem = ({ previousContainerHeight, currentContainerHeight, }) => previousContainerHeight !== currentContainerHeight;
let previousY = 0;
const useMemoizedHeaderContextValue = ({ viewIntersection, isStepOpen, }) => react_1.useMemo(() => {
    const intersectionRatio = lodash_1.get(viewIntersection, 'intersectionRatio', 1);
    const boundingClientRect = lodash_1.get(viewIntersection, 'boundingClientRect', {});
    const rootBounds = lodash_1.get(viewIntersection, 'rootBounds', {});
    const intersectionRect = lodash_1.get(viewIntersection, 'intersectionRect', {});
    const currentY = boundingClientRect.y;
    const isScrollingDown = currentY < previousY;
    const rootBoundsBottom = lodash_1.get(rootBounds, 'bottom');
    const intersectionRectBottom = lodash_1.get(intersectionRect, 'bottom');
    const isNotIntersectingAtBottom = !(intersectionRectBottom === rootBoundsBottom ||
        intersectionRectBottom > rootBoundsBottom - 100);
    const shouldCondenseAppBar = isScrollingDown &&
        isNotIntersectingAtBottom &&
        intersectionRatio === condenseAppBarThreshold;
    const shouldShowAppBarStepHeader = isScrollingDown &&
        isNotIntersectingAtBottom &&
        intersectionRatio < showAppBarStepHeaderThreshold;
    previousY = currentY;
    return !isStepOpen
        ? defaultHeaderContextValue
        : {
            shouldCondenseAppBar,
            shouldShowAppBarStepHeader,
            isAppBarStepHeaderHidden: !shouldShowAppBarStepHeader,
        };
}, [viewIntersection, isStepOpen]);
const useHeaderLock = () => {
    const [isHeaderLocked, updateHeaderLock] = react_1.useState(false);
    const lockHeader = react_1.useCallback(() => updateHeaderLock(true), [
        updateHeaderLock,
    ]);
    const unlockHeader = react_1.useCallback(() => updateHeaderLock(false), [
        updateHeaderLock,
    ]);
    return {
        isHeaderLocked,
        lockHeader,
        unlockHeader,
    };
};
exports.CurrentStepHeaderController = ({ children, isStepOpen, }) => {
    const [ref, , viewIntersection] = react_intersection_observer_1.useInView({
        threshold,
        root: document.documentElement,
    });
    const containerRef = react_1.useRef(null);
    const controllerContextValue = {
        ref,
        containerRef,
    };
    const currentContainerHeight = lodash_1.get(containerRef, 'current.scrollHeight', 0);
    const { isHeaderLocked, lockHeader, unlockHeader } = useHeaderLock();
    const currentHeaderContextValue = useMemoizedHeaderContextValue({
        viewIntersection,
        isStepOpen,
    });
    const [cachedHeaderContextValue, updateCachedHeaderContextValue] = react_1.useState(defaultHeaderContextValue);
    const [previousContainerHeight, setPreviousContainerHeight] = react_1.useState();
    const onScroll = react_1.useCallback(() => {
        const didSystemScrollOccur = wasScrolledBySystem({
            previousContainerHeight,
            currentContainerHeight,
        });
        if (didSystemScrollOccur) {
            setPreviousContainerHeight(currentContainerHeight);
            lockHeader();
        }
    }, [currentContainerHeight, lockHeader, previousContainerHeight]);
    const onWheel = react_1.useCallback(() => {
        updateCachedHeaderContextValue(currentHeaderContextValue);
        unlockHeader();
    }, [currentHeaderContextValue, unlockHeader]);
    const correctHeaderContextValue = isHeaderLocked
        ? cachedHeaderContextValue
        : currentHeaderContextValue;
    return (react_1.default.createElement(ControllerContext.Provider, { value: controllerContextValue },
        react_1.default.createElement(CurrentStepHeaderContext.Provider, { value: correctHeaderContextValue },
            react_1.default.createElement("div", { onScroll: onScroll, onWheel: onWheel }, children))));
};
exports.ControllerConnection = ({ children, isOpened, }) => {
    const { ref } = react_1.useContext(ControllerContext);
    const refProps = isOpened
        ? {
            ref,
        }
        : {};
    return react_1.default.createElement("div", Object.assign({}, refProps), children);
};
exports.CurrentStepHeaderControllerConnection = react_1.memo(exports.ControllerConnection);
exports.CurrentStepHeaderControllerScrollContainer = (_a) => {
    var { children } = _a, props = __rest(_a, ["children"]);
    const { containerRef } = react_1.useContext(ControllerContext);
    return (react_1.default.createElement("div", Object.assign({ className: "current-step-header-controller-scroll-observer" }, props, { ref: containerRef }), children));
};
